import type { EventStatus, PerformanceStatus, Permission } from '@bre-ticketing/contracts';

/**
 * Event and performance lifecycles (Phase 3). Small enough to read at a
 * glance, and the API refuses anything not listed here.
 */

export interface StatusTransition<S extends string> {
  readonly from: S;
  readonly to: S;
  readonly requiredPermission: Permission;
}

const t = <S extends string>(from: S, to: S, requiredPermission: Permission): StatusTransition<S> => ({ from, to, requiredPermission });

export const EVENT_TRANSITIONS: readonly StatusTransition<EventStatus>[] = Object.freeze([
  t<EventStatus>('DRAFT', 'PUBLISHED', 'events.publish'),
  t<EventStatus>('PUBLISHED', 'CANCELLED', 'events.publish'),
  t<EventStatus>('DRAFT', 'ARCHIVED', 'events.manage'),
  t<EventStatus>('PUBLISHED', 'ARCHIVED', 'events.manage'),
  t<EventStatus>('CANCELLED', 'ARCHIVED', 'events.manage'),
]);

/**
 * A performance is scheduled, goes on sale, may pause, ends sales, completes.
 * Canceling and postponing are allowed from any live state; a postponed
 * performance comes back as scheduled once it has a new date.
 */
export const PERFORMANCE_TRANSITIONS: readonly StatusTransition<PerformanceStatus>[] = Object.freeze([
  t<PerformanceStatus>('DRAFT', 'SCHEDULED', 'performances.manage'),
  t<PerformanceStatus>('SCHEDULED', 'ON_SALE', 'performances.manage'),
  t<PerformanceStatus>('ON_SALE', 'SALES_PAUSED', 'performances.manage'),
  t<PerformanceStatus>('SALES_PAUSED', 'ON_SALE', 'performances.manage'),
  t<PerformanceStatus>('ON_SALE', 'SALES_ENDED', 'performances.manage'),
  t<PerformanceStatus>('SALES_PAUSED', 'SALES_ENDED', 'performances.manage'),
  t<PerformanceStatus>('SALES_ENDED', 'ON_SALE', 'performances.manage'),
  t<PerformanceStatus>('SALES_ENDED', 'COMPLETED', 'performances.manage'),
  t<PerformanceStatus>('SCHEDULED', 'POSTPONED', 'performances.manage'),
  t<PerformanceStatus>('ON_SALE', 'POSTPONED', 'performances.manage'),
  t<PerformanceStatus>('SALES_PAUSED', 'POSTPONED', 'performances.manage'),
  t<PerformanceStatus>('SALES_ENDED', 'POSTPONED', 'performances.manage'),
  t<PerformanceStatus>('POSTPONED', 'SCHEDULED', 'performances.manage'),
  t<PerformanceStatus>('DRAFT', 'CANCELLED', 'events.publish'),
  t<PerformanceStatus>('SCHEDULED', 'CANCELLED', 'events.publish'),
  t<PerformanceStatus>('ON_SALE', 'CANCELLED', 'events.publish'),
  t<PerformanceStatus>('SALES_PAUSED', 'CANCELLED', 'events.publish'),
  t<PerformanceStatus>('SALES_ENDED', 'CANCELLED', 'events.publish'),
  t<PerformanceStatus>('POSTPONED', 'CANCELLED', 'events.publish'),
  /*
   * Rained out (D-374): from any state a game can be played or called in,
   * including after it was marked complete (the weather is sometimes decided
   * the next morning). Terminal. Only the rain-out action takes it, because it
   * is not only a status: every ticket on the game becomes a rain check.
   */
  t<PerformanceStatus>('SCHEDULED', 'RAINED_OUT', 'performances.rain_out'),
  t<PerformanceStatus>('ON_SALE', 'RAINED_OUT', 'performances.rain_out'),
  t<PerformanceStatus>('SALES_PAUSED', 'RAINED_OUT', 'performances.rain_out'),
  t<PerformanceStatus>('SALES_ENDED', 'RAINED_OUT', 'performances.rain_out'),
  t<PerformanceStatus>('COMPLETED', 'RAINED_OUT', 'performances.rain_out'),
]);

export function findTransition<S extends string>(
  transitions: readonly StatusTransition<S>[],
  from: S,
  to: S,
): StatusTransition<S> | undefined {
  return transitions.find((transition) => transition.from === from && transition.to === to);
}

/** Statuses a performance can be sold in, mirroring performance_statuses.public_sellable. */
export const SELLABLE_PERFORMANCE_STATUSES: readonly PerformanceStatus[] = Object.freeze(['ON_SALE']);
